/* eslint-disable no-throw-literal */
// CONSTANTS
import { ContentState } from "draft-js";
import { getContentStateFromRawSectionData } from "./components";

const alphaCharsList = [
  "A",
  "B",
  "C",
  "D",
  "E",
  "F",
  "G",
  "H",
  "I",
  "J",
  "K",
  "L",
  "M",
  "N",
  "O",
  "P",
  "Q",
  "R",
  "S",
  "T",
  "U",
  "V",
  "W",
  "X",
  "Y",
  "Z",
];

const allowedOperations = ["(", ")", "/", "*", "+", "-", "^", ":", ","];

const allowedMethods = ["SUM", "AVERAGE"];

export const validateFormulaString = formulaStr => {
  const preDefinedMethodIndex = allowedMethods.findIndex(method =>
    formulaStr.startsWith(method),
  );

  let formulaRegExp;
  const allowedCellChars = "A-Z0-9.";
  if (preDefinedMethodIndex > -1) {
    const allowedOperators = [":", ","];
    const allowedOperatorsChars = allowedOperators.join("");
    const method = allowedMethods[preDefinedMethodIndex];
    formulaRegExp = new RegExp(
      `${method}\\(([${allowedCellChars}${allowedOperatorsChars}]+)\\)`,
    );
  } else {
    const operators = ["(", ")", "+", "\\-", "*", "/", "^"];
    const allowedOperatorsChars = operators.join("");
    const regStr = `([${allowedCellChars}${allowedOperatorsChars}]+)`;
    formulaRegExp = new RegExp(regStr);
  }

  return formulaStr.match(formulaRegExp);
};

export const getAllowedOperators = formulaStr => {
  const preDefinedMethodIndex = allowedMethods.findIndex(method =>
    formulaStr.startsWith(method),
  );
  let allowedOperators;
  let allowedOpeningBrackets;
  let allowedClosingBrackets;
  let preDefinedMethod = null;
  if (preDefinedMethodIndex > -1) {
    preDefinedMethod = allowedMethods[preDefinedMethodIndex];
    allowedOperators = [":", ","];
    allowedOpeningBrackets = [];
    allowedClosingBrackets = [];
  } else {
    allowedOperators = ["+", "-", "*", "/", "^"];
    allowedOpeningBrackets = ["("];
    allowedClosingBrackets = [")"];
  }
  return {
    allowedOperators,
    allowedOpeningBrackets,
    allowedClosingBrackets,
    preDefinedMethod,
  };
};

export const getColumnIdentifier = columnIndex => {
  let colID = "";
  let val = columnIndex;
  while (val >= 26) {
    val /= 26;
    colID += String.fromCharCode(64 + val);
  }
  colID += String.fromCharCode(64 + (val % 26));
  return colID;
};

export const getRowIdentifier = rowIndex => rowIndex + 1;

export const getColumnIndexFromIdentifier = colIdentifier => {
  let pos = null;
  for (let i = 0; i < colIdentifier.length; i += 1) {
    const char = colIdentifier[i];
    const asciiVal = char.charCodeAt(0);
    pos = (pos === null ? 0 : pos + 1) * 26 + (asciiVal - 65);
  }
  return pos;
};

export const getRowIndexFromIdentifier = rowIdentifier =>
  Number(rowIdentifier) - 1;

export const getCellFromCellIdentifier = cell => {
  if (cell.row && cell.col) {
    return {
      rowIndex: getRowIndexFromIdentifier(cell.row),
      colIndex: getColumnIndexFromIdentifier(cell.col),
    };
  }
  return cell;
};

export const getAlphaPosition = alphaChar => {
  if (alphaCharsList.length === 1) {
    return alphaCharsList.indexOf(alphaChar);
  }
  let position;
  alphaChar.split("").forEach((char, index) => {
    position = index * 26 + alphaCharsList.indexOf(char);
  });

  return position;
};

export const getCellPositions = (appliedFormula, section) => {
  const cell = {
    row: "",
    col: "",
  };

  let num = "";
  const formulaWithCellId = [];

  const formulaRegExp = validateFormulaString(appliedFormula);

  const { preDefinedMethod } = getAllowedOperators(appliedFormula);

  const formula = formulaRegExp[1];

  if (preDefinedMethod) {
    formulaWithCellId.push(preDefinedMethod);
    formulaWithCellId.push("(");
  }

  for (let i = 0; i < formula.length; i += 1) {
    const char = formula[i];
    const asciiVal = char.charCodeAt(0);
    if (asciiVal > 64 && asciiVal < 91) {
      if (num) {
        formulaWithCellId.push(num);
        num = "";
      }
      cell.col += char;
    } else if (asciiVal > 47 && asciiVal < 58 && cell.col) {
      cell.row += char;
    } else {
      if (cell.row && cell.col) {
        const { rowIndex: row, colIndex: col } =
          getCellFromCellIdentifier(cell);
        const { id: cellId } = section.data.data[row].data[col].attributes;
        formulaWithCellId.push(cellId);
        cell.row = "";
        cell.col = "";
      }
      if (allowedOperations.includes(char)) {
        if (num) {
          formulaWithCellId.push(num);
          num = "";
        }
        formulaWithCellId.push(char);
      } else {
        num += char;
      }
    }
  }

  if (cell.row && cell.col) {
    const { rowIndex: row, colIndex: col } = getCellFromCellIdentifier(cell);
    const { id: cellId } = section.data.data[row].data[col].attributes;
    formulaWithCellId.push(cellId);
  }
  if (num) {
    formulaWithCellId.push(num);
    num = "";
  }

  if (preDefinedMethod) formulaWithCellId.push(")");

  return formulaWithCellId;
};

const isTableCellMerged = cell => {
  if (
    cell &&
    Object.prototype.hasOwnProperty.call(cell.attributes, "colspan") &&
    parseInt(cell.attributes.colspan, 10) > 1
  ) {
    return true;
  }
  return false;
};

const rowMergeData = section => {
  const mergeData = {};

  section.data.forEach((row, rowIndex) => {
    row.data.some((cell, idx) => {
      if (isTableCellMerged(cell)) {
        mergeData[rowIndex] = {};
        mergeData[rowIndex].mergePosition = idx;
        const mergedCellsSpan = [];
        for (
          let i = idx;
          i < idx + parseInt(cell.attributes.colspan, 10);
          i += 1
        ) {
          mergedCellsSpan.push(i);
        }
        mergeData[rowIndex].mergeSpan = mergedCellsSpan;
        mergeData[rowIndex].mergeSpanLength = mergedCellsSpan.length;
        return true;
      }
      return false;
    });
  });
  return mergeData;
};

const findNextVisibleColumn = (hiddenCols, colIndex, columnCount) => {
  if (colIndex >= columnCount) return null;
  if (!hiddenCols.includes(colIndex)) return colIndex;
  if (hiddenCols.includes(colIndex)) {
    return findNextVisibleColumn(hiddenCols, colIndex + 1, columnCount);
  }
  return null;
};

const findPrevVisibleColumn = (hiddenCols, colIndex) => {
  if (colIndex === -1) return null;

  if (!hiddenCols.includes(colIndex)) return colIndex;
  if (hiddenCols.includes(colIndex)) {
    return findPrevVisibleColumn(hiddenCols, colIndex - 1);
  }
  return null;
};

// {mergePosition: 1, mergeSpan: Array(6), mergeSpanLength: 6}

export const getCellIDtoPosition = section => {
  const positions = {};
  section.data.forEach((row, rowIndex) => {
    row.data.forEach((col, colIndex) => {
      positions[col.attributes.id] = [rowIndex, colIndex];
    });
  });
  return positions;
};

export const validateOperand = (operand, rowCount, colCount) => {
  const cell = {
    row: null,
    column: null,
  };

  for (let index = 0; index < operand.length; index += 1) {
    const char = operand[index];
    const asciiVal = char.charCodeAt(0);
    // check column identifier
    if (asciiVal > 64 && asciiVal < 91) {
      // check if row identifier is before column identifier
      if (cell.row !== null) return false;

      const colIndex = asciiVal - 64;
      cell.column =
        cell.column === null ? colIndex : cell.column * 26 + colIndex;
    } // check row identifier or a normal integer value
    else if (asciiVal > 47 && asciiVal < 58) {
      const rowIndex = asciiVal - 48;
      cell.row = cell.row === null ? rowIndex : cell.row * 10 + rowIndex;
    } else if (asciiVal === 46) {
      if (cell.column) return false;
    } else {
      return false;
    }
  }

  // check if the column identifier is correct
  if (cell.column) {
    if (cell.row === null) return false;

    if (cell.column > colCount) return false;

    if (cell.row === 0 || cell.row > rowCount) return false;
  }
  return true;
};

export const formulaValidation = (formulaExp, tableData) => {
  // col count row count
  // open brakcet before and closing after operator
  // validate alphaNum char
  try {
    if (!tableData || !tableData.data || !tableData.attributes) {
      throw "Invalid table data.";
    }

    const { rowCount, columnCount } = tableData.attributes;

    const formulaRegExp = validateFormulaString(formulaExp);

    if (!formulaRegExp) throw "Formula has invalid characters";

    // get expression string
    const formula = formulaRegExp[1];

    const { allowedOperators, allowedOpeningBrackets, allowedClosingBrackets } =
      getAllowedOperators(formulaExp);
    let operand1 = "";
    let operand2 = "";
    let operator = null;
    let openBracketCount = 0;

    for (let index = 0; index < formula.length; index += 1) {
      const character = formula[index];
      const asciiVal = character.charCodeAt(0);
      // check of opening bracket
      if (allowedOpeningBrackets.includes(character)) {
        openBracketCount += 1;
      }
      // check for closing brackets
      else if (allowedClosingBrackets.includes(character)) {
        openBracketCount -= 1;

        // check if there are more closing brackets than opening brackets
        if (openBracketCount < 0) throw `Invalid brackets at index ${index}`;

        // check if the closing bracket is just after
        if (!operand2) throw `Invalid brackets at index ${index}`;

        operand1 = operand1 + operator + operand2;
        operand2 = "";
        operator = null;
      }
      // check of opretors
      else if (allowedOperators.includes(character)) {
        // check if there is already a operator
        if (operator && !operand2)
          throw `Invalid opreator ${character} at ${index}`;

        // check if there is any operand1 or not
        if (!operand1) throw `Invalid operator ${character} at ${index}`;

        if (operand2) {
          if (!validateOperand(operand2, rowCount, columnCount))
            throw `Invalid cell identifier ${operand2}. Cell ID entered are beyond the table row and column range.`;

          // check if the cell identifier is right or not
          if (
            operand1.match(/^[A-Z0-9.]+$/) &&
            !validateOperand(operand1, rowCount, columnCount)
          )
            throw `Invalid cell identifier ${operand1}. Cell ID entered are beyond the table row and column range.`;

          operand1 = operand1 + operator + operand2;
          operand2 = "";
        }
        operator = character;
      }
      // check
      else if (
        (asciiVal > 64 && asciiVal < 91) ||
        (asciiVal > 47 && asciiVal < 58) ||
        asciiVal === 46
      ) {
        if (operator) {
          operand2 += character;
        } else {
          operand1 += character;
        }
      } else {
        throw `Invalid character at ${index}`;
      }
    }

    // check operand 1 and operand 2
    if (
      operand1 &&
      operand1.match(/^[A-Z0-9.]+$/) &&
      !validateOperand(operand1, rowCount, columnCount)
    )
      throw `Invalid cell identifier ${operand1}. Cell ID entered are beyond the table row and column range. `;

    if (operand2 && !validateOperand(operand2, rowCount, columnCount))
      throw `Invalid cell identifier ${operand2}. Cell ID entered are beyond the table row and column range. `;

    if (operator && !operand2) throw `Invalid expression near ${operator}`;

    if (openBracketCount !== 0) throw "Invalid numbers of brackets";
  } catch (error) {
    return {
      valid: false,
      message: `Please check syntax examples, ${error}`,
    };
  }
  return {
    valid: true,
  };
};

export const convertInfixToPostFix = appliedFormula => {
  // Conversion from infix expression ro postfix expression
  const stack = [];
  const postfixExpression = [];

  const precedence = {
    "(": 4,
    "^": 3,
    "/": 2,
    "*": 2,
    "+": 1,
    "-": 1,
  };

  //   Rules for the conversion from infix to postfix expression
  appliedFormula.forEach(currChar => {
    // If the stack is empty or contains a left parenthesis on top, push the incoming operator on to the stack.
    if (
      (stack.length === 0 || stack[stack.length - 1] === "(") &&
      allowedOperations.includes(currChar)
    ) {
      stack.push(currChar);
    }
    // If the incoming symbol is '(', push it on to the stack.
    else if (currChar === "(") {
      stack.push(currChar);
    }
    // If the incoming symbol is ')', pop the stack and push the operators until the left parenthesis is found.
    else if (currChar === ")") {
      let condition = true;
      while (condition) {
        if (stack[stack.length - 1] === "(") {
          condition = false;
          stack.pop();
        } else {
          postfixExpression.push(stack.pop());
        }
      }
    }
    // If the incoming symbol has higher precedence than the top of the stack, push it on the stack.
    else if (
      allowedOperations.includes(currChar) &&
      allowedOperations.includes(stack[stack.length - 1]) &&
      precedence[currChar] > precedence[stack[stack.length - 1]]
    ) {
      stack.push(currChar);
    }
    // If the incoming symbol has lower precedence than the top of the stack, pop and push the top of the stack. Then test the incoming operator against the new top of the stack.
    else if (
      allowedOperations.includes(currChar) &&
      allowedOperations.includes(stack[stack.length - 1]) &&
      precedence[currChar] < precedence[stack[stack.length - 1]]
    ) {
      let condition = true;
      while (condition) {
        if (
          precedence[currChar] >= precedence[stack[stack.length - 1]] ||
          stack.length === 0 ||
          stack[stack.length - 1] === "("
        ) {
          condition = false;
          stack.push(currChar);
        } else postfixExpression.push(stack.pop());
      }
    }
    // If the incoming operator has the same precedence with the top of the stack then use the associativity rules. If the associativity is from left to right then pop and push the top of the stack then push the incoming operator. If the associativity is from right to left then push the incoming operator.
    else if (
      allowedOperations.includes(currChar) &&
      allowedOperations.includes(stack[stack.length - 1]) &&
      precedence[currChar] === precedence[stack[stack.length - 1]]
    ) {
      if (currChar === "^") stack.push(currChar);
      else {
        postfixExpression.push(stack.pop());
        stack.push(currChar);
      }
    }
    // Push the operand as they arrive.
    else {
      postfixExpression.push(Number(currChar));
    }
  });

  // At the end of the expression, pop and push all the operators of the stack.
  if (stack.length > 0) {
    stack.reverse().forEach(currChar => postfixExpression.push(currChar));
  }

  // Evaluation of postfixExpression
  const valueStack = [];
  postfixExpression.forEach(val => {
    if (allowedOperations.includes(val)) {
      const firstOperand = valueStack.pop();
      const secondOperand = valueStack.pop();
      let ans = 0;
      if (val === "^") ans = secondOperand ** firstOperand;
      if (val === "/") ans = secondOperand / firstOperand;
      if (val === "*") ans = secondOperand * firstOperand;
      if (val === "+") ans = secondOperand + firstOperand;
      if (val === "-") ans = secondOperand - firstOperand;
      valueStack.push(ans);
    } else valueStack.push(val);
  });

  return valueStack[0];
};

export const getCellValue = (tableData, rowID, colID, mode) => {
  const { data: rData } = tableData;
  const rowData = rData[rowID];

  const { data: cData } = rowData;
  const { data: cellData, attributes } = cData[colID];
  if (attributes && attributes.appliedFormula) {
    // eslint-disable-next-line no-use-before-define
    return evaluateFormula(attributes.appliedFormula, tableData, mode);
  }
  const rawData = cellData.data;
  let contentState = null;
  if (rawData instanceof ContentState) {
    contentState = rawData;
  } else {
    contentState = getContentStateFromRawSectionData({ data: rawData }, mode);
  }
  if (contentState) {
    return contentState.getPlainText("\u000A");
  }
  return null;
};

export const checkCircularDependecy = (cellID, appliedFormula, tableData) => {
  const {
    attributes: { cellIDtoPosMapping },
  } = tableData;

  let method;
  let range = false;
  const dependentCellIDs = [];
  if (appliedFormula.includes(cellID)) return true;

  for (let index = 0; index < appliedFormula.length; index += 1) {
    const char = appliedFormula[index];
    if (char in cellIDtoPosMapping) {
      const cellPosition = cellIDtoPosMapping[char];

      // const { attributes: cellAttributes } = tableData.data[
      //   cellPosition[0]
      // ].data[cellPosition[1]];

      const cellAttributes =
        tableData.data[cellPosition[0]].data[cellPosition[1]].attributes;
      // if current char is a cell id string, check if it is equivalent of cell id
      if (char === cellID) return true;

      // check if the cell atttributes has a formula dependent on this cell id
      if (
        cellAttributes &&
        cellAttributes.appliedFormula &&
        (checkCircularDependecy(
          char,
          cellAttributes.appliedFormula,
          tableData,
        ) ||
          checkCircularDependecy(
            cellID,
            cellAttributes.appliedFormula,
            tableData,
          ))
      )
        return true;

      // check if it is a range or not
      if (!range) {
        dependentCellIDs.push(char);
      } else {
        const prevCellID = dependentCellIDs.pop();
        const prevCellPosition = cellIDtoPosMapping[prevCellID];
        if (prevCellPosition[0] === cellPosition[0]) {
          for (let i = prevCellPosition[1] + 1; i <= cellPosition[1]; i += 1) {
            const { attributes: currCellAttri } =
              tableData.data[cellPosition[0]].data[i];

            if (currCellAttri && currCellAttri.id === cellID) return true;

            // check if the cell atttributes has a formula dependent on this cell id
            if (
              currCellAttri &&
              currCellAttri.appliedFormula &&
              (checkCircularDependecy(
                cellID,
                currCellAttri.appliedFormula,
                tableData,
              ) ||
                checkCircularDependecy(
                  currCellAttri.id,
                  cellAttributes.appliedFormula,
                  tableData,
                ))
            )
              return true;
          }
        } else if (prevCellPosition[1] === cellPosition[1]) {
          for (let i = prevCellPosition[0] + 1; i <= cellPosition[0]; i += 1) {
            const { attributes: currCellAttri } =
              tableData.data[i].data[cellPosition[1]];
            if (currCellAttri && currCellAttri.id === cellID) return true;

            // check if the cell atttributes has a formula dependent on this cell id
            if (
              currCellAttri &&
              currCellAttri.appliedFormula &&
              (checkCircularDependecy(
                cellID,
                currCellAttri.appliedFormula,
                tableData,
              ) ||
                checkCircularDependecy(
                  currCellAttri.id,
                  currCellAttri.appliedFormula,
                  tableData,
                ))
            )
              return true;
          }
        }
      }
    } else if (allowedMethods.includes(char)) {
      method = char;
    } else if (method && char === ":") {
      range = true;
    }
  }
  return false;
};

export const evaluateFormula = (appliedFormula, tableData, mode) => {
  const {
    attributes: { cellIDtoPosMapping },
  } = tableData;
  const updatedFormula = [];

  let method;
  let cellCount = 0;
  let range = false;
  appliedFormula.forEach((char, index) => {
    if (char in cellIDtoPosMapping) {
      const cellPosition = cellIDtoPosMapping[char];
      if (method) cellCount += 1;
      if (!range) {
        const cellValue = getCellValue(
          tableData,
          cellPosition[0],
          cellPosition[1],
          mode,
        );
        updatedFormula.push(cellValue);
      } else {
        if (method) cellCount -= 1;
        const prevCellID = appliedFormula[index - 2];
        const prevCellPosition = cellIDtoPosMapping[prevCellID];
        if (prevCellPosition[0] === cellPosition[0]) {
          updatedFormula.pop();
          for (let i = prevCellPosition[1] + 1; i <= cellPosition[1]; i += 1) {
            if (method) cellCount += 1;
            const cellValue = getCellValue(tableData, cellPosition[0], i, mode);
            updatedFormula.push("+");
            updatedFormula.push(cellValue);
          }
        } else if (prevCellPosition[1] === cellPosition[1]) {
          updatedFormula.pop();
          for (let i = prevCellPosition[0] + 1; i <= cellPosition[0]; i += 1) {
            if (method) cellCount += 1;
            const cellValue = getCellValue(tableData, i, cellPosition[1], mode);
            updatedFormula.push("+");
            updatedFormula.push(cellValue);
          }
        }
      }
    } else if (allowedMethods.includes(char)) {
      method = char;
    } else {
      updatedFormula.push(char);

      if (method && char === ":") range = true;
    }
  });
  if (method) {
    if (method === "SUM" || method === "AVERAGE") {
      for (let i = 0; i < updatedFormula.length; i += 1) {
        if (updatedFormula[i] === ",") updatedFormula[i] = "+";
      }
    }
  }
  let result = convertInfixToPostFix(updatedFormula);

  if (method && method === "AVERAGE") {
    result /= cellCount;
  }

  result =
    Number(result) === result && result % 1 === 0
      ? result
      : Number(result.toFixed(4));
  return result;
};

export const getFormulaFromUUID = (uuidFormula, cellIDtoPosMapping) => {
  const appliedFormula = [];
  uuidFormula.forEach(char => {
    if (char.length > 16) {
      const cellPos = cellIDtoPosMapping[char];
      if (cellPos) {
        appliedFormula.push(alphaCharsList[cellPos[1]]);
        appliedFormula.push(cellPos[0] + 1);
      } else {
        appliedFormula.push("");
      }
    } else {
      appliedFormula.push(char);
    }
  });
  return appliedFormula.join("");
};

export const updateFormulaWhileCloning = clonedSection => {
  clonedSection.data.data.forEach(row => {
    row.data.forEach(col => {
      if (col.attributes.appliedFormula) {
        const {
          data: {
            attributes: { cellIDtoPosMapping },
          },
        } = clonedSection;
        let updatedFormula = getFormulaFromUUID(
          col.attributes.appliedFormula,
          cellIDtoPosMapping,
        );
        updatedFormula = getCellPositions(updatedFormula, clonedSection);
        col.attributes.appliedFormula = updatedFormula;
      }
    });
  });

  return clonedSection;
};

export const getCellNeighbours = section => {
  const {
    attributes: { rowCount, columnCount, hiddenCols },
    data,
  } = section;
  // To handle count value just after linking with external sheets
  const totalRowCount = data.length > rowCount ? data.length : rowCount;
  const totalColCount =
    data[0].data.length > columnCount ? data[0].data.length : columnCount;
  const cellNeighbours = {};
  const allMergedData = rowMergeData(section);
  for (let rowIndex = 0; rowIndex < totalRowCount; rowIndex++) {
    // column count will change for merged rows
    const currentRowMergeData = allMergedData[rowIndex];
    let columnCountValue = totalColCount;
    if (currentRowMergeData) {
      columnCountValue =
        totalColCount - currentRowMergeData.mergeSpan.length + 1;
    }
    for (let colIndex = 0; colIndex < columnCountValue; colIndex++) {
      // Finding neighbours
      const top = [rowIndex, colIndex];
      const right = [rowIndex, colIndex];
      const bottom = [rowIndex, colIndex];
      const left = [rowIndex, colIndex];
      // top
      if (rowIndex - 1 > -1) {
        // Normal table case (row number)
        top[0] = rowIndex - 1;

        // Calculating top if current row is merged
        if (
          currentRowMergeData &&
          colIndex >=
            currentRowMergeData.mergeSpan[
              currentRowMergeData.mergeSpan.length - 1
            ]
        ) {
          top[1] = colIndex + currentRowMergeData.mergeSpan.length - 1;
        }

        // Calculating for top row merged case
        const topRowMergedData = allMergedData[rowIndex - 1];
        if (topRowMergedData && topRowMergedData.mergeSpan.includes(colIndex)) {
          top[1] = topRowMergedData.mergePosition;
        }
        if (
          topRowMergedData &&
          colIndex >
            topRowMergedData.mergeSpan[topRowMergedData.mergeSpan.length - 1]
        ) {
          top[1] = colIndex - topRowMergedData.mergeSpan.length + 1;
        }
      }

      // bottom
      if (rowIndex + 1 < totalRowCount) {
        // Normal table row number
        bottom[0] = rowIndex + 1;

        // Calculating bottom if current row is merged
        if (
          currentRowMergeData &&
          colIndex >=
            currentRowMergeData.mergeSpan[
              currentRowMergeData.mergeSpan.length - 1
            ]
        ) {
          bottom[1] = colIndex + currentRowMergeData.mergeSpan.length - 1;
        }

        // Calculating for bottom row merged case
        const bottomRowMergedData = allMergedData[rowIndex + 1];
        if (
          bottomRowMergedData &&
          bottomRowMergedData.mergeSpan.includes(colIndex)
        ) {
          bottom[1] = bottomRowMergedData.mergePosition;
        }
        if (
          bottomRowMergedData &&
          colIndex >
            bottomRowMergedData.mergeSpan[
              bottomRowMergedData.mergeSpan.length - 1
            ]
        ) {
          bottom[1] = colIndex - bottomRowMergedData.mergeSpan.length + 1;
        }
      }

      // left
      if (colIndex - 1 > -1) {
        // Calculation left for hidden column cases
        if (hiddenCols && hiddenCols.includes(colIndex - 1)) {
          if (colIndex - 1 > 0)
            left[1] = findPrevVisibleColumn(hiddenCols, colIndex - 1);
          else if (rowIndex - 1 > -1) {
            left[0] = rowIndex - 1;
            left[1] = findPrevVisibleColumn(hiddenCols, totalColCount - 1);
          }
        } else left[1] = colIndex - 1;
      }
      // for cyclic rotation - left side
      if (colIndex - 1 === -1 && rowIndex - 1 > -1) {
        left[0] = rowIndex - 1;
        left[1] = totalColCount - 1;

        // Left side Cyclic navigation if top row is merged
        const topRowMergedData = allMergedData[rowIndex - 1];
        if (topRowMergedData) {
          const topColumnCount =
            totalColCount - topRowMergedData.mergeSpan.length + 1;
          left[1] = topColumnCount - 1;
        }

        // checking hidden columns
        if (hiddenCols && hiddenCols.includes(totalColCount - 1)) {
          left[1] = findPrevVisibleColumn(hiddenCols, totalColCount - 1);
        }
      }

      // right
      if (colIndex + 1 < totalColCount) {
        // Calculation right for hidden column cases
        if (hiddenCols && hiddenCols.includes(colIndex + 1)) {
          right[1] = findNextVisibleColumn(
            hiddenCols,
            colIndex + 1,
            totalColCount,
          );
          if (right[1] === null && rowIndex + 1 < totalRowCount) {
            right[0] = rowIndex + 1;
            right[1] = findNextVisibleColumn(hiddenCols, 0, totalColCount);
          }
        } else right[1] = colIndex + 1;
      }
      // for cyclic rotation - right side
      if (colIndex + 1 === columnCountValue && rowIndex + 1 < totalRowCount) {
        right[0] = rowIndex + 1;

        // checking hidden columns
        if (hiddenCols && hiddenCols.includes(0)) {
          right[1] = findNextVisibleColumn(hiddenCols, 1, totalColCount);
        } else right[1] = 0;
      }
      const myNeighbours = { top, right, bottom, left };
      cellNeighbours[`${rowIndex}-${colIndex}`] = myNeighbours;
    }
  }
  return cellNeighbours;
};
